import { Search } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import WorkflowCanvas from "./workflow-canvas";
import { flowchartCategories, workflowTemplates, workflowTemplatesHint, type WorkflowTemplate } from "@/mocks/workflows";

/* Галерея шаблонов рабочих процессов и карточка шаблона: слева описание, справа схема шагов. */

const CATEGORY_ICON: Record<string, string> = {
  autoreply: "ri-reply-all-line",
  routing: "ri-route-line",
  business: "ri-flow-chart",
  supervision: "ri-bar-chart-box-line",
  meta: "ri-meta-line",
  messenger: "ri-megaphone-line",
};

function TemplateThumb({ template }: { template: WorkflowTemplate }) {
  const icon = CATEGORY_ICON[template.category] ?? "ri-flow-chart";
  return (
    <div
      className="relative h-[5.5rem] flex-none border-b border-border bg-background/40"
      style={{
        backgroundImage: "radial-gradient(rgb(var(--f-fg) / 0.11) 1px, transparent 1px)",
        backgroundSize: "14px 14px",
      }}
      aria-hidden
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
        <span className="flex items-center gap-1 rounded-[5px] border border-border bg-card px-2 py-1">
          <i className="ri-flashlight-line text-[10px] leading-none text-red-500" />
          <span className="h-1 w-8 rounded-full bg-foreground/20" />
        </span>
        <span className="h-3 w-px bg-border" />
        <span className="flex gap-1.5">
          <span className="flex items-center gap-1 rounded-[5px] border border-border bg-card px-2 py-1">
            <i className={cn(icon, "text-[10px] leading-none text-primary")} />
            <span className="h-1 w-6 rounded-full bg-foreground/20" />
          </span>
          <span className="flex items-center gap-1 rounded-[5px] border border-border bg-card px-2 py-1">
            <i className="ri-send-plane-line text-[10px] leading-none text-primary" />
            <span className="h-1 w-6 rounded-full bg-foreground/20" />
          </span>
        </span>
      </div>
      {template.isNew ? (
        <span className="absolute start-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-[2px] text-[9px] font-semibold text-amber-700">
          <i className="ri-sparkling-line text-[9px] leading-none" aria-hidden />
          {t("Новый")}
        </span>
      ) : null}
    </div>
  );
}

type TemplatesProps = {
  category: string;
  query: string;
  onCategory: (id: string) => void;
  onQuery: (value: string) => void;
  onOpen: (template: WorkflowTemplate) => void;
  onScratch: () => void;
  onBack: () => void;
};

export function TemplatesView({ category, query, onCategory, onQuery, onOpen, onScratch, onBack }: TemplatesProps) {
  const q = query.trim().toLowerCase();
  const list = workflowTemplates
    .filter((t) => (category === "all" ? true : t.category === category))
    .filter((t) => (q ? t.name.toLowerCase().includes(q) || t.summary.toLowerCase().includes(q) : true));

  const count = (id: string) => (id === "all" ? workflowTemplates.length : workflowTemplates.filter((t) => t.category === id).length);

  return (
    <div className="flex min-w-0 flex-1">
      {/* категории */}
      <aside className="hidden w-[15.5rem] flex-none flex-col border-e border-border lg:flex">
        <div className="p-2.5">
          <label className="relative block">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder={t("Шаблоны поиска")}
              aria-label={t("Поиск по шаблонам")}
              className="h-8 w-full box-border rounded-btn border border-border bg-background ps-8 pe-2.5 text-[11.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </label>
        </div>

        <div className="flex min-h-0 flex-col gap-0.5 overflow-y-auto px-2 pb-2">
          {flowchartCategories.map((c) => {
            const active = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onCategory(c.id)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex w-full cursor-pointer items-start gap-2 rounded-btn px-2 py-[7px] text-start text-[11.5px] font-medium transition",
                  active ? "bg-primary/10 text-foreground" : "text-foreground/75 hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(c.icon, "mt-[1px] flex-none text-[13px] leading-none", active ? "text-primary" : "text-muted-foreground")} aria-hidden />
                <span className="min-w-0 flex-1 leading-snug">{c.label}</span>
                <span className={cn("flex-none text-[10px] tabular-nums", active ? "text-primary" : "text-muted-foreground/80")}>{count(c.id)}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-auto border-t border-border p-2.5">
          <p className="flex items-start gap-1.5 text-[10px] leading-relaxed text-muted-foreground">
            <i className="ri-information-line mt-0.5 flex-none text-[12px] leading-none text-primary" aria-hidden />
            {workflowTemplatesHint}
          </p>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start gap-2 border-b border-border px-3 py-2.5">
          <button
            type="button"
            onClick={onBack}
            aria-label={t("Назад к рабочим процессам")}
            title={t("Назад")}
            className="grid size-6 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <i className="ri-arrow-left-s-line text-[15px] leading-none" aria-hidden />
          </button>
          <div className="min-w-0 flex-1">
            <h4 className="text-[13.5px] font-semibold leading-snug">{t("Просмотрите шаблоны рабочих процессов")}</h4>
            <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">
              {t("Выберите шаблон, чтобы начать создание рабочего процесса.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onScratch}
            className="inline-flex h-7 flex-none cursor-pointer items-center justify-center whitespace-nowrap rounded-btn bg-primary px-3 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Начать с нуля")}
          </button>
        </div>

        {/* категории на мобильных */}
        <div className="flex items-center gap-1.5 overflow-x-auto border-b border-border px-2.5 py-2 lg:hidden">
          {flowchartCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onCategory(c.id)}
              className={cn(
                "inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10.5px] font-semibold transition",
                category === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
              )}
            >
              <i className={cn(c.icon, "text-[11px] leading-none")} aria-hidden />
              {c.label}
            </button>
          ))}
        </div>

        <div className="p-3 md:p-4">
          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-14 text-center">
              <span className="grid size-11 place-items-center rounded-full border border-border bg-background/60 text-muted-foreground">
                <i className="ri-search-line text-[17px] leading-none" aria-hidden />
              </span>
              <p className="mt-3 text-[12.5px] font-semibold">{t("Шаблоны не нашлись")}</p>
              <p className="mt-1 max-w-sm text-[11px] leading-relaxed text-muted-foreground">
                {t("Попробуйте другую категорию или очистите поиск — шаблоны ищутся по названию и описанию.")}
              </p>
              <button
                type="button"
                onClick={() => {
                  onQuery("");
                  onCategory("all");
                }}
                className="mt-3 inline-flex h-7 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card px-3 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary"
              >
                {t("Сбросить фильтры")}
              </button>
            </div>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
              {list.map((tpl) => (
                <article key={tpl.id} className="flex flex-col overflow-hidden rounded-btn border border-border bg-card transition hover:border-primary/40">
                  <TemplateThumb template={tpl} />
                  <div className="flex flex-1 flex-col p-2.5">
                    <h6 className="line-clamp-2 text-[11.5px] font-semibold leading-snug">{tpl.name}</h6>
                    <p className="mt-1 line-clamp-3 text-[10.5px] leading-relaxed text-muted-foreground">{tpl.summary}</p>
                    <button
                      type="button"
                      onClick={() => onOpen(tpl)}
                      className="mt-2.5 inline-flex h-7 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary"
                    >
                      {t("Используйте шаблон")}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type DetailProps = {
  template: WorkflowTemplate;
  onBack: () => void;
  onUse: (template: WorkflowTemplate) => void;
  onLearnMore: () => void;
};

export function TemplateDetail({ template, onBack, onUse, onLearnMore }: DetailProps) {
  const category = flowchartCategories.find((c) => c.id === template.category);

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("Назад к шаблонам")}
          title={t("Назад")}
          className="grid size-6 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <i className="ri-arrow-left-s-line text-[15px] leading-none" aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-[13.5px] font-semibold leading-snug">{template.name}</h4>
          <p className="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <i className={cn(CATEGORY_ICON[template.category] ?? "ri-flow-chart", "text-[11px] leading-none")} aria-hidden />
            {category?.label ?? t("Шаблон")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onUse(template)}
          className="inline-flex h-7 flex-none cursor-pointer items-center justify-center whitespace-nowrap rounded-btn bg-primary px-3 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110"
        >
          {t("Используйте шаблон")}
        </button>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 border-b border-border p-3.5 md:p-5 lg:border-b-0 lg:border-e">
          <p className="text-[11.5px] leading-relaxed text-foreground/80">{template.intro}</p>

          <h6 className="mt-4 text-[12px] font-semibold">{t("Преимущества")}</h6>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {template.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground/80">
                <span className="mt-1.5 size-1 flex-none rounded-full bg-primary" aria-hidden />
                {b}
              </li>
            ))}
          </ul>

          <h6 className="mt-4 text-[12px] font-semibold">{t("Как это работает")}</h6>
          <ol className="mt-1.5 flex flex-col gap-1.5">
            {template.how.map((h, i) => (
              <li key={h} className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground/80">
                <span className="grid size-4 flex-none place-items-center rounded-full bg-primary/[.08] text-[9px] font-bold text-primary tabular-nums">{i + 1}</span>
                {h}
              </li>
            ))}
          </ol>

          <button
            type="button"
            onClick={onLearnMore}
            className="mt-4 inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-primary underline decoration-dotted underline-offset-2 transition hover:brightness-110"
          >
            <i className="ri-book-open-line text-[12px] leading-none" aria-hidden />
            {t("Узнайте больше об этом шаблоне")}
          </button>
        </div>

        <div className="min-w-0 p-2.5 md:p-3">
          <WorkflowCanvas steps={template.steps} className="min-h-[24rem]" />
        </div>
      </div>
    </div>
  );
}